import { useMemo } from 'react';
import { cn } from '../../common/functions/cn';
import { parseDate } from '../../common/functions/parse-date';
import useTestTag from '../../common/hooks/use-test-tag';
import InnerHTMLDiv from '../common/inner-html-div.component';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';

const USER_TIME_ZONE = Intl.DateTimeFormat(navigator.language).resolvedOptions().timeZone;
const DATE_FORMATTER = Intl.DateTimeFormat(navigator.language, { dateStyle: 'short', timeZone: USER_TIME_ZONE });
const TIME_FORMATTER = Intl.DateTimeFormat(navigator.language, { timeStyle: 'short', timeZone: USER_TIME_ZONE });

export interface CommentElementProps {
  id?: string | number;
  draft?: boolean;
  imageSrc?: string;
  user: string;
  date: string;
  message: string;
  readOnly?: boolean;
  formatDate?: (date: Date) => string;
}

const _formatDisplayDate = (date: Date | null) => {
  if (!date) {
    return '-';
  }
  return DATE_FORMATTER.format(date);
};

const _formatDisplayTime = (date: Date | null) => {
  if (!date) {
    return '-';
  }
  return TIME_FORMATTER.format(date);
};

export default function CommentElement(props: CommentElementProps): React.JSX.Element {
  const { id, user, date, message, readOnly, formatDate } = props;
  const _date = useMemo(() => parseDate(date), [date]);

  const container = {
    wrapper: 'pt-2 mt-2 flex border-t border-border',
    image: 'rounded-full',
    avatar: 'mt-px',
    details: 'flex flex-col w-full',
    user: cn('font-medium text-sm', {
      'text-muted-foreground': readOnly,
    }),
    dateWrapper: 'flex text-xs my-2',
    timeIcon: 'ml-4',
    date: 'mr-1 ml-1.5',
    message: 'mt-3 mb-5 text-sm text-justify font-normal',
  };

  const tagCreator = useTestTag('comment-element');

  return (
    <div key={id} className={container.wrapper} ref={tagCreator()}>
      <div className={container.details}>
        <h3 className={container.user}>{user}</h3>
        <div className={container.dateWrapper}>
          <OdinIcon icon={'CalendarCheck'} size={OdinIconSize.ExtraSmall} />
          <p className={container.date}>{formatDate && _date ? formatDate(_date) : _formatDisplayDate(_date)}</p>
          <OdinIcon icon={'Clock'} size={OdinIconSize.ExtraSmall} className={container.timeIcon} />
          <p className={container.date}>{_formatDisplayTime(_date)}</p>
        </div>
        <InnerHTMLDiv html={message ?? ''} />
      </div>
    </div>
  );
}
